import { Injectable, ElementRef, ViewContainerRef } from '@angular/core';
import { GeofenceData, GeoManagerCaptions, ProcessedMarkerGroup, MarkerData } from './geo-manager.interfaces';

@Injectable({
  providedIn: 'root'
})
export class GeofenceUIBuilderService {

  constructor() { }

  /**
   * Creates the main geofence drawer UI
   */
  createGeofenceDrawer(
    containerRef: ElementRef,
    templateRef: ElementRef,
    cleanupFn: (container: ElementRef) => void
  ): HTMLElement {
    cleanupFn(containerRef);
    const containerElement = containerRef.nativeElement;

    const drawerElement = templateRef.nativeElement.cloneNode(true) as HTMLElement;
    containerElement.appendChild(drawerElement);

    return drawerElement;
  }

  /**
   * Updates the geofence drawer content with the current geofence list
   */
  updateGeofenceDrawerContent(
    containerRef: ElementRef,
    geofences: GeofenceData[],
    templateRef: ElementRef,
    isGeofenceEditMode: boolean,
    createGeofenceElementFn: (geofence: GeofenceData) => HTMLElement,
    restoreOriginalDrawerStructureFn: (drawerElement: HTMLElement) => void,
    toggleElementVisibilityFn: (element: HTMLElement, visible: boolean) => void
  ): void {
    console.log('updateGeofenceDrawerContent called, isGeofenceEditMode:', isGeofenceEditMode);
    
    const containerElement = containerRef?.nativeElement;
    if (!containerElement) {
      console.log('No container element found');
      return;
    }

    const drawerElement = containerElement.querySelector('.geofence-drawer-panel');
    if (!drawerElement) {
      console.log('No drawer element found');
      return;
    }

    // Check if we need to restore the original drawer structure
    const editFormElement = drawerElement.querySelector('.geofence-edit-form');
    if (editFormElement) {
      console.log('Edit form found, need to restore original drawer structure');
      restoreOriginalDrawerStructureFn(drawerElement);
      return;
    }

    const geofenceListElement: HTMLElement = drawerElement.querySelector('.geofences-list');
    const emptyStateElement: HTMLElement = drawerElement.querySelector('.no-geofences-message');

    if (!geofenceListElement || !emptyStateElement) {
      console.log('List elements not found, need to restore drawer structure');
      restoreOriginalDrawerStructureFn(drawerElement);
      return;
    }

    if (geofences.length === 0) {
      toggleElementVisibilityFn(geofenceListElement, false);
      toggleElementVisibilityFn(emptyStateElement, true);
    } else {
      toggleElementVisibilityFn(geofenceListElement, true);
      toggleElementVisibilityFn(emptyStateElement, false);

      geofenceListElement.innerHTML = '';
      geofences.forEach(geofence => {
        const geofenceElement = createGeofenceElementFn(geofence);
        geofenceListElement.appendChild(geofenceElement);
      });
    }
    
    console.log('updateGeofenceDrawerContent completed successfully');
  }

  /**
   * Creates a geofence item element for the list
   */
  createGeofenceElement(
    geofence: GeofenceData,
    templateRef: ElementRef,
    processedGroups: ProcessedMarkerGroup[],
    captions: GeoManagerCaptions,
    getGeofenceTypeLabelFn: (type: string) => string,
    selectGeofenceFn: (geofence: GeofenceData) => void,
    editGeofenceFn: (geofenceId: string) => void,
    executeGeofenceRemoveActionFn: (geofence: GeofenceData) => void,
    calculateMarkersInGeofenceFn: (geofence: GeofenceData, markers: MarkerData[]) => number
  ): HTMLElement {
    const template = templateRef.nativeElement.cloneNode(true) as HTMLElement;
    
    const nameElement = template.querySelector('.geofence-name');
    if (nameElement) nameElement.textContent = geofence.name;

    const colorElement = template.querySelector('.geofence-color') as HTMLElement;
    if (colorElement) {
      const color = geofence.color || geofence.style?.color || '#3B82F6';
      colorElement.style.backgroundColor = color;
    }

    // Calculate and display marker count with type
    const allMarkers = processedGroups.flatMap(group => group.markers);
    const markerCount = calculateMarkersInGeofenceFn(geofence, allMarkers);
    const markerCountElement = template.querySelector('.marker-count');
    if (markerCountElement) {
      const markersLabel = captions.markersCountLabel || 'markers';
      const countText = `${markerCount} ${markersLabel}`;
      markerCountElement.textContent = countText;
    }

    const typeElement = template.querySelector('.geofence-type');
    if (typeElement) {
      typeElement.textContent = getGeofenceTypeLabelFn(geofence.type);
    }

    // Add click listener to the main geofence item for centering
    const mainContent = template.querySelector('.flex-1');
    if (mainContent) {
      mainContent.addEventListener('click', () => {
        selectGeofenceFn(geofence);
      });
      // Add cursor pointer to indicate it's clickable
      (mainContent as HTMLElement).style.cursor = 'pointer';
    }

    // Add event listeners for edit and delete
    const editBtn = template.querySelector('.edit-geofence-btn');
    if (editBtn) {
      editBtn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent triggering the main click
        editGeofenceFn(geofence.id);
      });
    }

    const removeBtn = template.querySelector('.remove-geofence-btn');
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent triggering the main click
        executeGeofenceRemoveActionFn(geofence);
      });
    }

    return template;
  }

  /**
   * Shows the drawing mode interface
   */
  showDrawingModeInterface(
    type: 'polygon' | 'rectangle' | 'circle',
    containerRef: ElementRef,
    templateRef: ElementRef,
    getGeofenceTypeLabelFn: (type: string) => string,
    cancelDrawingModeFn: () => void,
    addEscapeKeyListenerFn: () => void
  ): void {
    const drawerElement = containerRef.nativeElement.querySelector('.geofence-drawer-panel');
    if (!drawerElement) return;
    
    // Find the main container that should be replaced (everything except the title)
    const drawerBody = drawerElement.querySelector('.geo-drawer-content');
    if (!drawerBody) return;

    // Clone the drawing mode template
    const drawingTemplate = templateRef.nativeElement.cloneNode(true) as HTMLElement;
    drawingTemplate.style.display = 'block';
    
    // Update the title and icon based on type
    const titleElement = drawingTemplate.querySelector('.drawing-mode-title');
    const iconElement = drawingTemplate.querySelector('.drawing-icon svg');
    
    if (titleElement) {
      titleElement.textContent = `Dibujando ${getGeofenceTypeLabelFn(type)}`;
    }
    
    if (iconElement) {
      // Update icon based on type - replace the entire SVG content
      let svgContent = '';
      
      switch (type) {
        case 'polygon':
          svgContent = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5v16h18V3l-10.5 7.5z"/>';
          break;
        case 'rectangle':
          svgContent = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4h16v16H4z"/>';
          break;
        case 'circle':
          svgContent = '<circle cx="12" cy="12" r="10" stroke-width="2"/>';
          break;
      }
      
      if (svgContent) {
        iconElement.innerHTML = svgContent;
      }
    }

    // Add event listeners
    const cancelBtn = drawingTemplate.querySelector('.cancel-drawing-mode-btn');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => cancelDrawingModeFn());
    }

    // Replace the entire body content
    drawerBody.innerHTML = '';
    drawerBody.appendChild(drawingTemplate);
    
    // Add ESC key listener
    addEscapeKeyListenerFn();
  }

  /**
   * Shows the creation form with drawn geofence data
   */
  showCreateFormWithDrawnGeofence(
    geofence: GeofenceData,
    containerRef: ElementRef,
    templateRef: ElementRef,
    getGeofenceTypeLabelFn: (type: string) => string,
    addGeofenceFormEventListenersFn: (formElement: HTMLElement, geofence: GeofenceData, isEditMode: boolean) => void
  ): void {
    const drawerElement = containerRef.nativeElement.querySelector('.geofence-drawer-panel');
    if (!drawerElement) return;
    
    const drawerBody = drawerElement.querySelector('.geo-drawer-content');
    if (!drawerBody) return;

    // Clone the creation form template
    const formTemplate = templateRef.nativeElement.cloneNode(true) as HTMLElement;
    formTemplate.style.display = 'block';
    
    // Set the form title
    const titleElement = formTemplate.querySelector('.form-title');
    if (titleElement) {
      titleElement.textContent = `Crear ${getGeofenceTypeLabelFn(geofence.type)}`;
    }

    // Set values from the geofence
    const nameInput = formTemplate.querySelector('.geofence-name-input') as HTMLInputElement;
    const descriptionInput = formTemplate.querySelector('.geofence-description-input') as HTMLTextAreaElement;
    const colorInput = formTemplate.querySelector('.geofence-color-input') as HTMLInputElement;
    const colorDisplay = formTemplate.querySelector('.geofence-color-display');

    if (nameInput) {
      nameInput.value = geofence.name;
    }
    if (descriptionInput) {
      descriptionInput.value = geofence.description;
    }
    if (colorInput) {
      colorInput.value = geofence.color;
    }
    if (colorDisplay) {
      colorDisplay.textContent = geofence.color;
    }

    // Hide the "Dibujar en Mapa" button since it's already drawn
    const drawBtn = formTemplate.querySelector('.start-drawing-btn') as HTMLElement;
    if (drawBtn) {
      drawBtn.style.display = 'none';
    }

    // Show the save button since we have coordinates
    const saveBtn = formTemplate.querySelector('.save-geofence-btn') as HTMLElement;
    if (saveBtn) {
      saveBtn.style.display = 'inline-block';
    }

    // Add form event listeners
    addGeofenceFormEventListenersFn(formTemplate, geofence, false);
    
    // Replace the entire body content with form
    drawerBody.innerHTML = '';
    drawerBody.appendChild(formTemplate);
  }

  /**
   * Shows the geofence edit mode form
   */
  showGeofenceEditMode(
    geofence: GeofenceData,
    containerRef: ElementRef,
    templateRef: ElementRef,
    getGeofenceTypeLabelFn: (type: string) => string,
    addGeofenceFormEventListenersFn: (formElement: HTMLElement, geofence: GeofenceData, isEditMode: boolean) => void
  ): void {
    const drawerElement = containerRef.nativeElement.querySelector('.geofence-drawer-panel');
    if (!drawerElement) return;
    
    const drawerBody = drawerElement.querySelector('.geo-drawer-content');
    if (!drawerBody) return;

    // Clone the creation form template (same as create mode)
    const formTemplate = templateRef.nativeElement.cloneNode(true) as HTMLElement;
    formTemplate.style.display = 'block';
    
    // Set the form title for edit mode
    const titleElement = formTemplate.querySelector('.form-title');
    if (titleElement) {
      titleElement.textContent = `Editar ${getGeofenceTypeLabelFn(geofence.type)}`;
    }

    // Set values from the existing geofence
    const nameInput = formTemplate.querySelector('.geofence-name-input') as HTMLInputElement;
    const descriptionInput = formTemplate.querySelector('.geofence-description-input') as HTMLTextAreaElement;
    const colorInput = formTemplate.querySelector('.geofence-color-input') as HTMLInputElement;
    const colorDisplay = formTemplate.querySelector('.geofence-color-display');

    if (nameInput) {
      nameInput.value = geofence.name;
    }
    if (descriptionInput) {
      descriptionInput.value = geofence.description || '';
    }
    if (colorInput) {
      const color = geofence.color || geofence.style?.color || '#3B82F6';
      colorInput.value = color;
    }
    if (colorDisplay) {
      const color = geofence.color || geofence.style?.color || '#3B82F6';
      colorDisplay.textContent = color;
    }

    // Hide the "Dibujar en Mapa" button since geofence already exists
    const drawBtn = formTemplate.querySelector('.start-drawing-btn') as HTMLElement;
    if (drawBtn) {
      drawBtn.style.display = 'none';
    }

    // Show the save button since we have coordinates
    const saveBtn = formTemplate.querySelector('.save-geofence-btn') as HTMLElement;
    if (saveBtn) {
      saveBtn.style.display = 'inline-block';
    }

    // Add form event listeners for edit mode
    addGeofenceFormEventListenersFn(formTemplate, geofence, true);
    
    // Replace the entire body content with form
    drawerBody.innerHTML = '';
    drawerBody.appendChild(formTemplate);
  }

  /**
   * Restores the full drawer structure
   */
  restoreFullDrawerStructure(
    containerRef: ElementRef,
    templateRef: ElementRef,
    addGeofenceDrawerEventListenersFn: (drawerElement: HTMLElement) => void,
    updateGeofenceDrawerContentFn: () => void
  ): void {
    const drawerElement = containerRef.nativeElement.querySelector('.geofence-drawer-panel');
    if (!drawerElement) return;
    
    const drawerBody = drawerElement.querySelector('.h-full.flex.flex-col');
    if (!drawerBody) return;

    // Restore the original structure from the template
    const originalTemplate = templateRef.nativeElement.cloneNode(true) as HTMLElement;
    const originalBody = originalTemplate.querySelector('.h-full.flex.flex-col');
    
    if (originalBody) {
      drawerBody.innerHTML = originalBody.innerHTML;
      
      // Re-add event listeners to the restored buttons
      addGeofenceDrawerEventListenersFn(drawerElement as HTMLElement);
      
      // Update the drawer content with current geofences
      updateGeofenceDrawerContentFn();
    }
  }

  /**
   * Restores the original drawer structure
   */
  restoreOriginalDrawerStructure(
    drawerElement: HTMLElement,
    templateRef: ElementRef,
    addGeofenceDrawerEventListenersFn: (drawerElement: HTMLElement) => void,
    toggleElementVisibilityFn: (element: HTMLElement, visible: boolean) => void
  ): void {
    console.log('Restoring original drawer structure');
    
    // Get the original template content
    const template = templateRef.nativeElement.cloneNode(true) as HTMLElement;
    const originalContent = template.querySelector('.geo-drawer-content');
    
    if (originalContent) {
      // Replace the entire content with the original structure
      const currentContent = drawerElement.querySelector('.geo-drawer-content');
      if (currentContent) {
        currentContent.innerHTML = originalContent.innerHTML;

        // Update visibility for restored elements
        const geofenceListElement: HTMLElement = currentContent.querySelector('.geofences-list');
        const emptyStateElement: HTMLElement = currentContent.querySelector('.no-geofences-message');
        
        if (geofenceListElement && emptyStateElement) {
          toggleElementVisibilityFn(geofenceListElement, true);
          toggleElementVisibilityFn(emptyStateElement, false);
        }
        
        // Re-attach event listeners for the restored content
        addGeofenceDrawerEventListenersFn(drawerElement);
      }
    }
    
    console.log('Original drawer structure restored');
  }

  /**
   * Gets the localized label for a geofence type
   */
  getGeofenceTypeLabel(type: string): string {
    switch (type) {
      case 'polygon': return 'Polígono';
      case 'rectangle': return 'Rectángulo';
      case 'circle': return 'Círculo';
      default: return type;
    }
  }

  /**
   * Generates a unique ID for geofences
   */
  generateId(): string {
    return 'geofence_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
}
